export interface RuntimeCheck {
  database: "ok" | "error";
  serviceTypeCount: number;
}

export interface WorkerHealth {
  service: "background-worker";
  status: "ok";
  environment: string;
  checks: RuntimeCheck;
}

export interface PlatformHealth {
  service: "pages-functions";
  status: "ok" | "degraded";
  environment: string;
  requestId: string;
  checks: {
    appDatabase: RuntimeCheck;
    workerBinding: "ok" | "error";
    worker: WorkerHealth | null;
  };
}

export interface ServiceTypeSummary {
  id: string;
  slug: string;
  name: string;
  sortOrder: number;
}

export type DvrUserRole =
  | "viewer"
  | "editor"
  | "manager"
  | "administrator";
export type DvrUserStatus = "pending" | "active" | "disabled";
export type DvrScopeMode = "all" | "selected";

export type DvrPermissionKey =
  | "view_service_types"
  | "view_live_video"
  | "play_videos"
  | "restore_archived_videos"
  | "edit_event_metadata"
  | "edit_phase1b_metadata"
  | "add_video_comments"
  | "manage_video_visibility"
  | "manage_retention_flags"
  | "adjust_capture_windows"
  | "manage_one_time_events"
  | "manage_schedules"
  | "manage_service_configuration"
  | "manage_users"
  | "manage_global_settings"
  | "permanently_delete_media"
  | "share_custom_links";

export interface RolePermissionDefinition {
  key: DvrPermissionKey;
  label: string;
  description: string;
  implementationStatus: "available" | "planned";
  sortOrder: number;
  configurable: boolean;
}

export interface RolePermissionGrant {
  role: DvrUserRole;
  permissionKey: DvrPermissionKey;
  enabled: boolean;
  locked: boolean;
}

export interface RolePermissionsResponse {
  permissions: RolePermissionDefinition[];
  grants: RolePermissionGrant[];
}

export interface UpdateRolePermissionsRequest {
  role: Exclude<DvrUserRole, "administrator">;
  permissionKeys: DvrPermissionKey[];
}

export interface PublicSiteSettings {
  organizationName: string;
  siteName: string;
  themeTemplate: "default" | "custom" | "friends-church-online";
  backgroundColor: string;
  fontColor: string;
  featureColor: string;
  fontFamily: "DM Sans" | "Arial" | "Georgia" | "Newsreader" | "PT Serif" | "System";
  logoDataUrl: string | null;
  videoCommentsEnabled: boolean;
  viewingAnalyticsEnabled: boolean;
}

export interface PublicSiteSettingsResponse extends PublicSiteSettings {
  environment: string;
}

export type GeneralSettingsResponse = PublicSiteSettings;
export type UpdateGeneralSettingsRequest = PublicSiteSettings;

export interface AdminUserSummary {
  id: string;
  name: string;
  email: string;
  role: DvrUserRole | null;
  scopeMode: DvrScopeMode;
  status: DvrUserStatus;
  serviceTypeIds: string[];
  approvedAt: string | null;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
  isCurrentUser: boolean;
}

export interface AdminServiceTypeSummary extends ServiceTypeSummary {
  isActive: boolean;
}

export interface AdminUsersResponse {
  users: AdminUserSummary[];
  serviceTypes: AdminServiceTypeSummary[];
}

export interface UpdateUserAccessRequest {
  status: "active" | "disabled";
  role: DvrUserRole | null;
  scopeMode: DvrScopeMode;
  serviceTypeIds: string[];
}

export interface UpdateUserAccessResponse {
  user: AdminUserSummary;
}

export interface CreateUserInvitationRequest {
  recipientEmail: string;
  role: DvrUserRole;
  scopeMode: DvrScopeMode;
  serviceTypeIds: string[];
}

export interface CreateUserInvitationResponse {
  invitation: {
    id: string;
    recipientEmail: string;
    role: DvrUserRole;
    scopeMode: DvrScopeMode;
    serviceTypeIds: string[];
    expiresAt: string;
  };
}

export interface AdminUserInvitationSummary {
  id: string;
  recipientEmail: string;
  role: DvrUserRole;
  scopeMode: DvrScopeMode;
  serviceTypeIds: string[];
  emailStatus: "queued" | "sending" | "sent" | "failed";
  expiresAt: string;
  createdAt: string;
}

export interface AdminUserInvitationsResponse {
  invitations: AdminUserInvitationSummary[];
}

export interface ServiceTypeConfiguration extends AdminServiceTypeSummary {
  description: string | null;
  pcoServiceTypeId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceTypeConfigurationInput {
  slug: string;
  name: string;
  description: string | null;
  pcoServiceTypeId: string | null;
  sortOrder: number;
  isActive: boolean;
}

export interface ServiceTypesConfigurationResponse {
  serviceTypes: ServiceTypeConfiguration[];
}

export interface ServiceTypeConfigurationResponse {
  serviceType: ServiceTypeConfiguration;
}

export interface LiveInputConfiguration {
  id: string;
  name: string;
  cloudflareLiveInputUid: string;
  sourceLabel: string | null;
  notes: string | null;
  recordingSegmentationMode: "continuous" | "cycle_between_events";
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LiveInputConfigurationInput {
  name: string;
  cloudflareLiveInputUid: string;
  sourceLabel: string | null;
  notes: string | null;
  recordingSegmentationMode: "continuous" | "cycle_between_events";
  isActive: boolean;
}

export interface LiveInputsConfigurationResponse {
  liveInputs: LiveInputConfiguration[];
}

export interface LiveInputConfigurationResponse {
  liveInput: LiveInputConfiguration;
}

export interface ScheduleConfiguration {
  id: string;
  serviceTypeId: string;
  serviceTypeName: string;
  liveInputId: string | null;
  liveInputName: string | null;
  name: string;
  weekday: number;
  startTimeLocal: string;
  endTimeLocal: string;
  timezone: "America/New_York";
  preRollMinutes: number;
  postRollMinutes: number;
  effectiveStartDate: string | null;
  effectiveEndDate: string | null;
  hasCustomThumbnail: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ScheduleConfigurationInput {
  serviceTypeId: string;
  liveInputId: string | null;
  name: string;
  weekday: number;
  startTimeLocal: string;
  endTimeLocal: string;
  timezone: "America/New_York";
  preRollMinutes: number;
  postRollMinutes: number;
  effectiveStartDate: string | null;
  effectiveEndDate: string | null;
  isActive: boolean;
}

export interface SchedulesConfigurationResponse {
  schedules: ScheduleConfiguration[];
}

export interface ScheduleConfigurationResponse {
  schedule: ScheduleConfiguration;
}

export interface EventGenerationRequest {
  startDateLocal?: string;
  daysAhead?: number;
}

export interface EventGenerationResult {
  startDateLocal: string;
  endDateExclusiveLocal: string;
  daysAhead: number;
  schedulesEvaluated: number;
  occurrencesMatched: number;
  eventsCreated: number;
  existingEvents: number;
  videosCreated: number;
}

export type DvrEventStatus = "scheduled" | "live" | "ended" | "cancelled" | "failed";
export type DvrMediaStatus = "waiting" | "processing" | "ready" | "restoring" | "failed" | "deleted";
export type DvrStorageStatus = "none" | "online" | "archive_pending" | "online_archived" | "archived" | "restore_pending" | "deleted";

export const cloudflarePlaybackStorageStatuses = ["online", "archive_pending", "online_archived"] as const satisfies
  readonly DvrStorageStatus[];

export const isCloudflarePlaybackStorageStatus = (status: DvrStorageStatus): boolean =>
  cloudflarePlaybackStorageStatuses.some((playableStatus) => playableStatus === status);
export type DvrEventIngestMethod = "live_capture" | "file_upload";
export type EventThumbnailMode = "pco" | "custom" | "abbreviation";
export type PcoPlanAssociationMode = "automatic" | "manual";

export interface EventManualTag {
  id: string;
  value: string;
}

export interface EventPcoPlanOption {
  id: string;
  planDateLocal: string | null;
  label: string;
  seriesTitle: string | null;
}

export interface EventLibraryItem {
  id: string;
  serviceTypeId: string;
  serviceTypeName: string;
  thumbnailMode: EventThumbnailMode;
  thumbnailUrl: string | null;
  title: string;
  eventDateLocal: string;
  scheduledStartAt: string;
  scheduledEndAt: string;
  eventStatus: DvrEventStatus;
  videoId: string;
  mediaStatus: DvrMediaStatus;
  storageStatus: DvrStorageStatus;
  isManual: boolean;
  ingestMethod: DvrEventIngestMethod;
  needsReview: boolean;
  isHidden: boolean;
  transcriptMatch?: {
    startMs: number;
    text: string;
  };
}

export interface EventLibraryResponse {
  events: EventLibraryItem[];
  total: number;
  facets: EventLibraryFacets;
}

export type EventFacetKey =
  | "serviceType"
  | "storage"
  | "series"
  | "pastor"
  | "song"
  | "songLeader"
  | "emcee"
  | "tag";

export interface EventFacetValue {
  value: string;
  label: string;
  count: number;
}

export type EventLibraryFacets = Record<EventFacetKey, EventFacetValue[]>;

export interface EventDetailResponse {
  event: EventLibraryItem & {
    notes: string | null;
    captureStartAt: string;
    captureEndAt: string;
    durationSeconds: number | null;
    keepOnline: boolean;
    protectFromDeletion: boolean;
    onlineExpiresAt: string | null;
    archiveExpiresAt: string | null;
    manualTags: EventManualTag[];
    pcoPlanId: string | null;
    pcoPlanUrl: string | null;
    pcoPlanAssociationMode: PcoPlanAssociationMode;
    pcoPlanLabel: string | null;
    pcoPlanOptions: EventPcoPlanOption[];
    videoNotice: { message: string; reviewedAt: string } | null;
  };
}

export interface EventPlanSummaryItem {
  id: string;
  sequence: number;
  title: string;
  itemType: string | null;
  description: string | null;
  lengthSeconds: number | null;
  assignedPeople: Array<{ name: string; position: string | null }>;
}

export interface EventPlanSummaryResponse {
  event: { id: string; title: string; serviceTypeName: string; scheduledStartAt: string; planLabel: string };
  plan: { seriesTitle: string | null; dateLabel: string | null; people: Array<{ name: string; position: string | null; team: string | null }>; items: EventPlanSummaryItem[] };
}

export interface CreateEventManualTagRequest {
  value: string;
}

export interface UpdateEventPlanAssociationRequest {
  mode: PcoPlanAssociationMode;
  pcoPlanId: string | null;
}

export interface UpdateEventPlanAssociationResponse {
  eventId: string;
  mode: PcoPlanAssociationMode;
  pcoPlanId: string | null;
  pcoPlanLabel: string | null;
}

export interface OneTimeEventInput {
  serviceTypeId: string;
  liveInputId: string;
  title: string;
  eventDateLocal: string;
  scheduledStartTimeLocal: string;
  scheduledEndTimeLocal: string;
  preRollMinutes: number;
  postRollMinutes: number;
  notes: string | null;
}

export interface PostDateEventInput {
  serviceTypeId: string;
  title: string;
  eventDateLocal: string;
  scheduledStartTimeLocal: string;
  scheduledEndTimeLocal: string;
  notes: string | null;
}

export interface EventUploadRequest {
  fileName: string;
  fileSizeBytes: number;
  mimeType: string;
}

export interface EventUploadProvisionResponse {
  eventId: string;
  videoId: string;
  uploadUrl: string;
  streamAssetUid: string;
  expiresAt: string;
  status: "uploading";
}

export interface EventUploadStatusResponse {
  eventId: string;
  videoId: string;
  streamAssetUid: string;
  status: "uploading" | "processing" | "ready" | "failed" | "cancelled";
  uploadedBytes: number;
  expectedSizeBytes: number;
  errorCode: string | null;
}

export interface EventMetadataInput {
  title: string;
  notes: string | null;
}

export interface EventCaptureWindowInput {
  eventDateLocal: string;
  scheduledStartTimeLocal: string;
  scheduledEndTimeLocal: string;
  preRollMinutes: number;
  postRollMinutes: number;
}

export interface OneTimeEventOptionsResponse {
  serviceTypes: Array<{ id: string; name: string }>;
  liveInputs: Array<{ id: string; name: string }>;
}

export interface LiveEventPlaybackResponse {
  eventId: string;
  playerUrl: string;
  liveInputName: string;
}

export interface EventLifecycleResult {
  becameLive: number;
  becameEnded: number;
  reconciledAt: string;
}

export interface RetentionSettingsResponse {
  onlineDays: number;
  restoredOnlineDays: number;
  archiveMode: "never";
}

export interface UpdateRetentionSettingsRequest {
  onlineDays: number;
  restoredOnlineDays: number;
}

export interface VideoControlsResponse {
  videoId: string;
  isHidden: boolean;
  keepOnline: boolean;
  protectFromDeletion: boolean;
  onlineExpiresAt: string | null;
  archiveExpiresAt: string | null;
}

export interface UpdateVideoControlsRequest {
  isHidden: boolean;
  keepOnline: boolean;
  protectFromDeletion: boolean;
}

export interface DeleteEventRequest {
  reason: string;
  confirmation: "DELETE";
}

export interface DeleteEventResponse {
  eventId: string;
  videoId: string;
  eventStatus: DvrEventStatus;
  status: "deleted" | "already_deleted";
  assetsDeleted: number;
}

export interface OperationalWorkflow {
  id: string;
  workflowType: string;
  entityType: string;
  entityId: string;
  status: "queued" | "running" | "waiting" | "succeeded" | "failed" | "cancelled";
  attemptCount: number;
  errorCode: string | null;
  serviceTypeName: string | null;
  eventTitle: string | null;
  updatedAt: string;
  retryable: boolean;
}

export interface OperationalSourceRecording {
  id: string;
  liveInputName: string;
  recordingUid: string;
  status: string;
  startedAt: string | null;
  cleanupAfter: string | null;
  expectedVideos: number;
  safeVideos: number;
  resolvedVideos: number;
  failedVideos: number;
  cleanupEligible: boolean;
  unresolvedVideos: Array<{ videoId: string; eventTitle: string; errorCode: string | null }>;
}

export interface OperationalLiveInput {
  id: string;
  name: string;
  cloudflareLiveInputUid: string;
  isActive: boolean;
  segmentationMode: "continuous" | "cycle_between_events";
  providerEnabled: boolean | null;
  connectionState: "connected" | "disconnected" | "unknown";
  providerStatusEnteredAt: string | null;
  lastCycle: {
    status: string;
    disabledAt: string | null;
    enabledAt: string | null;
    errorCode: string | null;
  } | null;
  nextCycle: { disableAt: string; nextEventStartsAt: string } | null;
  nextCapture: {
    eventTitle: string;
    startsAt: string;
    endsAt: string;
    isUnderway: boolean;
  } | null;
  requiresAttention: boolean;
}

export interface OperationalIndexedMedia {
  eventId: string;
  videoId: string;
  eventTitle: string;
  serviceTypeName: string;
  scheduledStartAt: string;
  mediaStatus: DvrMediaStatus;
  durationMs: number | null;
  uploadStatus: "uploading" | "processing" | "ready" | "failed" | "cancelled" | null;
  uploadProgressPercent: number | null;
  uploadErrorCode: string | null;
  transcriptStatus: TranscriptStatus | null;
  transcriptProcessedMs: number | null;
  transcriptProgressPercent: number | null;
  transcriptErrorCode: string | null;
  ocrStatus: "aligned" | "unreadable" | "failed" | null;
  ocrDetectedTimer: string | null;
  ocrDetectedStartMs: number | null;
  ocrErrorCode: string | null;
  ocrCheckedAt: string | null;
  markerCount: number;
  confirmedMarkerCount: number;
  autoVerifiedMarkerCount: number;
  lowConfidenceMarkerCount: number;
  captureReviewRequired: boolean;
  reviewState: "processing" | "failed" | "needs_review" | "reviewed" | "ready";
  reviewedAt: string | null;
  videoErrorCode: string | null;
  updatedAt: string;
}

export interface OperationalStorageInventory {
  videoCount: number;
  totalDurationSeconds: number;
  unknownDurationCount: number;
  totalBytes: number;
  unknownSizeCount: number;
  oldestVideoAt: string | null;
  newestVideoAt: string | null;
}

export interface MarkerAccuracyTolerance {
  seconds: 2 | 5 | 10 | 30;
  count: number;
  percent: number;
}

export interface MarkerAccuracyGroup {
  label: string;
  sampleCount: number;
  medianAbsoluteErrorMs: number;
  p90AbsoluteErrorMs: number;
  within10SecondsPercent: number;
  falseHighConfidenceCount: number;
}

export interface MarkerAccuracyCorrectionPattern {
  serviceTypeName: string;
  planItemType: string;
  planItemTitle: string;
  sampleCount: number;
  largeCorrectionCount: number;
  medianAbsoluteErrorMs: number;
  p90AbsoluteErrorMs: number;
}

export interface MarkerAccuracyCorrectionOutlier {
  videoId: string;
  scheduledStartAt: string;
  serviceTypeName: string;
  planItemType: string;
  planItemTitle: string;
  signedCorrectionMs: number;
  absoluteErrorMs: number;
  confirmedAt: string;
}

export interface MarkerAccuracyReport {
  sampleCount: number;
  videoCount: number;
  latestConfirmedAt: string | null;
  medianAbsoluteErrorMs: number | null;
  p90AbsoluteErrorMs: number | null;
  tolerances: MarkerAccuracyTolerance[];
  falseHighConfidenceCount: number;
  byServiceType: MarkerAccuracyGroup[];
  byPlanItemType: MarkerAccuracyGroup[];
  repeatedCorrectionPatterns: MarkerAccuracyCorrectionPattern[];
  largestCorrections: MarkerAccuracyCorrectionOutlier[];
}

export interface ShadowMetricSet {
  medianAbsoluteErrorMs: number | null;
  p90AbsoluteErrorMs: number | null;
  falseHighConfidenceCount: number;
}

export interface ShadowEvaluationGroup {
  label: string;
  sampleCount: number;
  baseline: ShadowMetricSet;
  shadow: ShadowMetricSet;
  medianChangeMs: number | null;
  p90ChangeMs: number | null;
  materiallyRegressed: boolean;
}

export interface ShadowActivationCriterion {
  key: "representative_coverage" | "median_improved" | "p90_improved" |
    "false_high_confidence_not_increased" | "no_material_group_regression" |
    "reasoning_queue_healthy" | "reasoning_accuracy_non_regressing";
  label: string;
  passed: boolean;
  detail: string;
}

export interface ShadowEvaluationReport {
  solverVersion: string | null;
  runCount: number;
  videoCount: number;
  referenceCount: number;
  latestEvaluatedAt: string | null;
  baseline: ShadowMetricSet;
  shadow: ShadowMetricSet;
  byServiceType: ShadowEvaluationGroup[];
  byPlanItemType: ShadowEvaluationGroup[];
  reasoning: {
    eligibleBoundaryCount: number;
    completedCount: number;
    candidateCount: number;
    ambiguousCount: number;
    pendingCount: number;
    failedCount: number;
    historical: {
      eligibleBoundaryCount: number;
      completedCount: number;
      candidateCount: number;
      ambiguousCount: number;
      pendingCount: number;
      failedCount: number;
      solverMedianAbsoluteErrorMs: number | null;
      reasonedMedianAbsoluteErrorMs: number | null;
      solverP90AbsoluteErrorMs: number | null;
      reasonedP90AbsoluteErrorMs: number | null;
      solverWithin10SecondsCount: number;
      reasonedWithin10SecondsCount: number;
      improvedCount: number;
      worsenedCount: number;
      unchangedCount: number;
    };
  };
  gate: {
    status: "awaiting_evidence" | "gate_not_met" | "candidate_for_release_review";
    criteria: ShadowActivationCriterion[];
    requiresExplicitReleaseDecision: true;
  };
}

export type MarkerSolverReleaseStatus =
  "prepared" | "activating" | "active" | "rolling_back" | "rolled_back" | "discarded" | "failed";

export interface MarkerSolverReleaseSummary {
  id: string;
  solverVersion: string;
  status: MarkerSolverReleaseStatus;
  videoCount: number;
  proposalCount: number;
  changeCount: number;
  skippedUnchangedCount: number;
  preparedAt: string;
  activatedAt: string | null;
  rolledBackAt: string | null;
  discardedAt: string | null;
  lastError: string | null;
}

export interface MarkerSolverReleaseResponse extends Omit<MarkerSolverReleaseSummary, "lastError"> {}

export interface OperationsResponse {
  summary: {
    failed: number;
    running: number;
    waiting: number;
    cleanupEligibleSources: number;
    blockedSources: number;
    liveInputAlerts: number;
    indexedMediaProcessing: number;
    indexedMediaFailed: number;
    lowConfidenceMarkers: number;
  };
  workflows: OperationalWorkflow[];
  sourceRecordings: OperationalSourceRecording[];
  liveInputs: OperationalLiveInput[];
  indexedMedia: OperationalIndexedMedia[];
  markerAccuracy: MarkerAccuracyReport;
  shadowEvaluation: ShadowEvaluationReport;
  markerSolverRelease: MarkerSolverReleaseSummary | null;
  storage: {
    cloudflare: OperationalStorageInventory & { processingVideoCount: number };
    googleDrive: OperationalStorageInventory & { pendingArchiveVideoCount: number };
  };
}

export interface RetryWorkflowResponse {
  workflowId: string;
  workflowType: string;
  result: unknown;
}

export interface AuditLogEntry {
  id: string;
  occurredAt: string;
  actorUserId: string | null;
  actorName: string | null;
  actorEmail: string | null;
  action: string;
  targetType: string;
  targetId: string | null;
  serviceTypeId: string | null;
  serviceTypeName: string | null;
  details: Record<string, unknown> | null;
}

export interface AuditLogResponse {
  entries: AuditLogEntry[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
  filters: {
    actors: Array<{ id: string; name: string; email: string }>;
    actions: string[];
    targetTypes: string[];
  };
}

export interface VideoPlaybackResponse {
  videoId: string;
  playerUrl: string;
}

export interface VideoComment {
  id: string;
  authorName: string;
  body: string;
  createdAt: string;
  isPrivate: boolean;
  canDelete: boolean;
}

export interface VideoCommentsResponse {
  videoId: string;
  comments: VideoComment[];
}

export interface CreateVideoCommentRequest {
  body: string;
  isPrivate?: boolean;
}

export interface VideoViewingAnalytics {
  uniqueViewers: number;
  views: number;
  minutesWatched: number;
}

export interface VideoViewingAnalyticsResponse {
  videoId: string;
  live: VideoViewingAnalytics;
  recorded: VideoViewingAnalytics;
  viewers: Array<{ name: string; email: string; liveMinutes: number; recordedMinutes: number }>;
}

export type TranscriptStatus = "queued" | "processing" | "ready" | "failed" | "deleted";
export type MarkerType = "plan_item" | "manual" | "section";
export type MarkerSource = "pco_alignment" | "manual" | "system";
export const markerReviewConfidenceThreshold = 0.65;

export interface TranscriptSegment {
  id: string;
  sequence: number;
  startMs: number;
  endMs: number;
  text: string;
  confidence: number | null;
}

export interface VideoTranscript {
  id: string;
  language: string;
  status: TranscriptStatus;
  processedMs: number | null;
  segments: TranscriptSegment[];
}

export interface VideoMarker {
  id: string;
  pcoPlanItemId: string | null;
  pcoPlanItemTitle: string | null;
  markerType: MarkerType;
  label: string;
  startMs: number;
  endMs: number | null;
  source: MarkerSource;
  confidence: number | null;
  isConfirmed: boolean;
  /** Strict, reversible system verification. This is never a human confirmation. */
  isAutoVerified: boolean;
}

export interface VideoPlanItem {
  id: string;
  sequence: number | null;
  title: string;
  itemType: string | null;
  lengthSeconds: number | null;
}

export interface VideoIndexedMediaResponse {
  videoId: string;
  durationMs: number | null;
  transcript: VideoTranscript | null;
  markers: VideoMarker[];
  planItems: VideoPlanItem[];
}

export interface UpdateTranscriptSegmentRequest {
  startMs: number;
  endMs: number;
  text: string;
}

export interface MarkerInput {
  pcoPlanItemId: string | null;
  markerType: MarkerType;
  label: string;
  startMs: number;
  endMs: number | null;
  isConfirmed: boolean;
}

export interface PcoServicesSyncResult {
  serviceTypesProcessed: number;
  plansProcessed: number;
  itemsProcessed: number;
  peopleProcessed: number;
  assignmentsProcessed: number;
  eventsMatched: number;
  markersProposed: number;
}

export type PcoServicesSyncFrequency =
  | "manual"
  | "weekly"
  | "daily"
  | "every_four_hours";

export interface PcoServicesSyncSettingsResponse {
  frequency: PcoServicesSyncFrequency;
  timezone: "America/New_York";
  dailyTimeLocal: "15:00";
  weeklyDay: "Thursday";
  lastSuccessfulSyncAt: string | null;
}

export interface UpdatePcoServicesSyncSettingsRequest {
  frequency: PcoServicesSyncFrequency;
}
